import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnInit,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { pick } from 'lodash';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { EDCStatusMap } from '@widgets/edc-status-banner/const/edc-mapper.const';
import {
  EDCItem,
  EDCItemStatus,
  EDCUploadStatus,
} from '@widgets/edc-status-banner/types/edc-status.type';
import { getEDCUploadStatusFromFiles } from '@widgets/edc-status-banner/utils/edc-status-from-files.utils';

import { FirstNameShowComponent } from '@features/first-name-show/first-name-show.component';

import { File } from '@shared/components/file-manager/state/file.model';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ApiService } from '@shared/services/api.service';
import {
  DocumentType,
  listDocumentsQuery,
  listUserNamesWithEmailQuery,
  User,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowStepType } from '@shared/store/workflow/workflow.const';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { EDCProperties, Workflow } from '@shared/store/workflow/workflow.store';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { Utils } from '@shared/utils/utils';

import { ChecklistEdcDocumentConfirmationComponent } from './checklist-edc-document-confirmation.component';
import { ChecklistUploadEdcDocumentComponent } from './checklist-upload-edc-document/checklist-upload-edc-document.component';
import { ChecklistComponentData } from '../../models/quarter-close-checklist.model';
import { ChecklistRowInfoComponent } from '../checklist-row-info/checklist-row-info.component';

@Component({
  imports: [CommonModule, TooltipDirective, FirstNameShowComponent],
  styleUrls: ['../../quarter-close-checklist.component.scss'],
  templateUrl: './checklist-section-gather-patients.component.html',
})
export class ChecklistSectionGatherPatientsComponent implements ChecklistComponentData, OnInit {
  apiService = inject(ApiService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  quarterCloseChecklistPeriodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  router = inject(Router);
  workflowQuery = inject(WorkflowQuery);
  workflowService = inject(WorkflowService);

  private injector = inject(Injector);

  workflow = computed(() => {
    return this.workflowQuery.getWorkflowByStepType(
      WorkflowStepType.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
    )();
  });
  edcPropertiesFromWorkFlow = computed(() => {
    const wf = this.workflow();

    if (!wf) return {} as EDCProperties;
    const edcProperties = this.selectEDCProperties(wf);

    return edcProperties;
  });
  isEDCStatusAvailableFromWorkflow = computed(() => {
    const wf = this.workflow();

    if (!wf) {
      return false;
    }

    const edcProperties = this.selectEDCProperties(wf);

    return !!edcProperties.edc_data_status;
  });
  isReviewInvestigatorDataLinkOpen = computed(() => {
    return this.edcPropertiesFromWorkFlow().edc_data_status === EDCItemStatus.STATUS_AVAILABLE;
  });

  data: unknown;
  edcUploadDetails$ = new BehaviorSubject<EDCUploadStatus>({
    date_uploaded: 'emptystring',
    uploaded_by: '',
  });
  id = 'GatherPatients';
  isWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStepType.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );
  loading$ = new BehaviorSubject<boolean>(false);
  monthStatus: 'closed' | 'open' = 'closed';
  parent!: ChecklistRowInfoComponent;
  statusItem$ = new BehaviorSubject<EDCItem>({ bgColor: '', borderColor: '', color: '', text: '' });
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();

  ngOnInit(): void {
    void this.setInitialState();

    const MODAL_DELAY = 1000;

    runInInjectionContext(this.injector, () => {
      const disabled = signal(this.isWorkflowLocked() || this.monthStatus !== 'open');

      injectExecutableByRoute(() => {
        document.getElementById('checklistInvestigator')?.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => {
          void this.openUploadEDCModal();
        }, MODAL_DELAY);
      }, disabled);
    });
  }

  constructor() {
    effect(() => {
      if (
        this.workflow() &&
        this.edcPropertiesFromWorkFlow() &&
        this.isEDCStatusAvailableFromWorkflow()
      ) {
        this.setStatesFromWorkflow();
      }
    });
    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((_users) => {
        _users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });
    this.monthStatus = dayjs(
      this.quarterCloseChecklistPeriodCloseService.selectedQuarterMonth
    ).isSame(this.quarterCloseChecklistPeriodCloseService.currentOpenMonth)
      ? 'open'
      : 'closed';
  }

  dateFormatter = (date: string) => Utils.dateFormatter(date);

  defaultPath = () => {
    const trial_id = this.mainQuery.getValue().trialKey;

    return `trials/${trial_id}/checklist`;
  };

  async fetchFiles(): Promise<listDocumentsQuery['items']> {
    const files = await this.apiService.getFilesByFilters(
      this.pathFN(),
      undefined,
      undefined,
      DocumentType.DOCUMENT_MANUAL_TRANSACTION_SUPPORT,
      undefined,
      undefined
    );

    return files;
  }

  getDisabledTooltip() {
    if (this.monthStatus !== 'open') {
      return 'Can only upload EDC data for current month.';
    }

    if (this.isWorkflowLocked()) {
      return MessagesConstants.EDC_DATA_LOCKED;
    }

    return '';
  }

  onProtocolAmendments() {
    void this.router.navigateByUrl(
      `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX}`
    );
  }

  onReviewInvestigatorData() {
    void this.router.navigateByUrl(
      `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_TRANSACTIONS}`
    );
  }

  onSites() {
    void this.router.navigateByUrl(
      `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}`
    );
  }

  async openUploadEDCModal() {
    const date = dayjs(this.parent.workflow.properties.month).format('MMMM YYYY');

    const resp = await firstValueFrom(
      this.overlayService.openPopup<
        { workflow: Workflow },
        {
          success: boolean;
        },
        ChecklistUploadEdcDocumentComponent
      >({
        content: ChecklistUploadEdcDocumentComponent,
        data: { workflow: this.parent.workflow },
        settings: {
          header: `Upload EDC Documents - ${date}`,
          primaryButton: {
            action: async (instance) => {
              await instance?.uploadFiles();
            },
            disabled: (instance) => {
              if (!instance) {
                return false;
              }

              const filesNotUploaded = instance.filesNotUploaded();
              const removedFiles = instance.removedFiles$.value;

              return (
                !filesNotUploaded ||
                !removedFiles ||
                (!removedFiles.length && !filesNotUploaded.length)
              );
            },
          },
        },
      }).afterClosed$
    );

    if (resp.data?.success) {
      this.overlayService.openPopup({
        content: ChecklistEdcDocumentConfirmationComponent,
        settings: {
          header: 'Document(s) Uploaded Successfully!',
          primaryButton: {
            label: 'OK',
          },
          secondaryButton: {
            hidden: true,
          },
        },
      });
    } else {
      void this.setInitialState();
    }
  }

  pathFN = () => {
    const selectedMonth = this.quarterCloseChecklistPeriodCloseService.selectedQuarterMonth;

    return `${this.defaultPath()}/${selectedMonth}/`;
  };

  selectEDCProperties(workflow: Workflow) {
    const edcProperties = {
      ...pick(workflow.properties, [
        'edc_data_status',
        'edc_data_status_update_date',
        'edc_data_status_updated_by',
        'last_customer_notified_date',
        'last_customer_notified_by',
      ]),
    } as EDCProperties;

    return edcProperties;
  }

  async setInitialState() {
    if (!this.workflow() || !this.isEDCStatusAvailableFromWorkflow()) {
      this.loading$.next(true);
      const files = await this.fetchFiles();

      this.setStatesFromFiles(files);
      this.loading$.next(false);
    }
  }

  setStatesFromFiles(savedFiles: File[] | listDocumentsQuery['items']) {
    const files = savedFiles;

    this.setStatusState(
      files.length > 0 ? EDCItemStatus.STATUS_PROCESSING : EDCItemStatus.STATUS_AWAITING_UPLOAD
    );
    this.edcUploadDetails$.next(getEDCUploadStatusFromFiles(files));
  }

  setStatesFromWorkflow() {
    const edcProperties = this.edcPropertiesFromWorkFlow();
    const updateDate = edcProperties.edc_data_status_update_date;
    const updatedBy = edcProperties.edc_data_status_updated_by;
    const status = edcProperties.edc_data_status;

    this.setStatusState(status as EDCItemStatus);
    this.edcUploadDetails$.next({
      date_uploaded: updateDate || 'kk',
      uploaded_by: updatedBy || '',
    });
  }

  setStatusState(status: EDCItemStatus): void {
    this.statusItem$.next(EDCStatusMap[status]);
  }

  userFormatter(sub: string | undefined) {
    const user = this.users.get(sub || '');

    return user ? `${user.given_name} ${user.family_name}` : Utils.zeroHyphen;
  }
}
